import React, { useCallback, useEffect, useId, useState } from "react";
import { getReleaseMetadata } from "../lib/releaseMetadata";
import logoFallback from "../images/KrossKast_Logo-red.png";
import "./SidebarReleaseFlipCard.css";

/**
 * Bottom-sidebar flip card: active Brand chrome ↔ KrossKast release metadata.
 * When no Brand is active, shows the KrossKast logo (release side still works).
 */
export default function SidebarReleaseFlipCard({
  logoUrl = null,
  displayName = null,
  brandId = null,
  metadata = null,
}) {
  const [flipped, setFlipped] = useState(false);
  const [imgFailed, setImgFailed] = useState(false);
  const meta = metadata || getReleaseMetadata();
  const labelId = useId();

  useEffect(() => {
    setImgFailed(false);
  }, [logoUrl]);

  const toggle = useCallback(() => setFlipped((v) => !v), []);
  const onKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      toggle();
    }
  };

  const envLabel =
    meta.environment === "unknown"
      ? "LOCAL"
      : String(meta.environment).toUpperCase();
  const name = (displayName || "Account").trim() || "Account";
  const showBrandLogo = Boolean(logoUrl) && !imgFailed;
  const hasActiveBrand = Boolean(
    String(brandId || "").trim() && String(displayName || "").trim(),
  );

  return (
    <div
      className="kk-release-flip-slot"
      data-testid="sidebar-release-flip-card"
      data-active-brand={hasActiveBrand ? "true" : "false"}
      data-brand-id={brandId || undefined}
    >
      <div
        className={`kk-release-flip${flipped ? " flipped" : ""}`}
        role="button"
        tabIndex={0}
        aria-pressed={flipped}
        aria-labelledby={labelId}
        aria-label={
          flipped
            ? `Release ${meta.releaseId}, ${meta.environment}, commit ${meta.gitSha}. Activate to show logo.`
            : hasActiveBrand
              ? `Active brand ${name}. Activate to show release details.`
              : "Account logo. Activate to show release details."
        }
        title={flipped ? "Click to show logo" : "Click to show release version"}
        onClick={toggle}
        onKeyDown={onKeyDown}
      >
        <span id={labelId} className="visually-hidden">
          {flipped
            ? "Release details"
            : hasActiveBrand
              ? `Active brand ${name}`
              : "Account logo"}
        </span>
        <div className="kk-release-flip-inner">
          <div className="kk-release-flip-front" aria-hidden={flipped}>
            {showBrandLogo ? (
              <div
                className="kk-release-flip-brand"
                data-testid="active-brand-chrome"
              >
                <img
                  className="kk-release-flip-logo"
                  src={logoUrl}
                  alt=""
                  data-testid="active-brand-logo"
                  onError={() => setImgFailed(true)}
                />
                {displayName ? (
                  <span
                    className="kk-release-flip-name"
                    title={name}
                    data-testid="active-brand-name"
                  >
                    {name}
                  </span>
                ) : null}
              </div>
            ) : displayName ? (
              <span
                className="kk-release-flip-name"
                title={name}
                data-testid="active-brand-name"
              >
                {name}
              </span>
            ) : (
              <img
                className="kk-release-flip-logo"
                src={logoFallback}
                alt="KrossKast"
              />
            )}
          </div>
          <div className="kk-release-flip-back" aria-hidden={!flipped}>
            <div className="kk-release-flip-meta">
              <div className="kk-release-flip-line">{meta.releaseId}</div>
              {meta.buildTimestamp ? (
                <div className="kk-release-flip-line">{meta.buildTimestamp}</div>
              ) : null}
              <div className="kk-release-flip-line">{envLabel}</div>
              <div className="kk-release-flip-line">{meta.gitSha}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
